import { SelectionSlot } from '../context/personalisation-typedefs';
import { PersonalisationChoice } from '../gateway/addToCart';
import { fetchImageAsFile } from './addImagesToFormData';
import { supportsWebP } from './webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

interface ImagePropertiesProcessingProps {
  visData: {
    url: string | File;
    file: File | null;
    visualiserSlotMode: string;
    groupId: string;
    lineItemPropertyKey: string;
    label: string;
    positioning?: { x: number; y: number; scale: number } | null;
  }[];
  personalisationChoices: PersonalisationChoice[];
  selections?: {
    slots: SelectionSlot[];
  } | null;
}

export const imagePropertiesProcessing = async ({
  visData,
  personalisationChoices,
  selections = null
}: ImagePropertiesProcessingProps) => {
  // Track which _featured_image_X corresponds to which ALPHANUMERIC slot for later processing in addImageUrls.
  const featuredImageMapping = {};
  const featuredImageProperties = (
    await Promise.all(
      visData
        // Convert any urls to files.
        .map((vis, index) => {
          if (vis.file instanceof File) {
            return Promise.resolve({ ...vis, file: vis.file });
          }
          if (typeof vis.url === 'string') {
            return fetchImageAsFile(vis.url + '&w=1000', `featured-image-${index}.${imageType.split('/').at(1)}`).then(
              (file) => ({
                ...vis,
                file
              })
            );
          }
          // vis.url is a File
          return Promise.resolve({ ...vis, file: vis.url as File });
        })
    )
  ).reduce((acc, vis) => {
    const featuredImageKey = vis.lineItemPropertyKey;
    if (!featuredImageKey) return acc;

    // Skip photo variant IMAGE_UPLOAD entries — their LIP keys are managed
    // by the File-based reduce (supporting key) and supportingImageProperties (primary key).
    // Without this guard, URL-type visData entries (e.g. "Photo Insert - Visual") would be
    // added to flat cart properties and eventually leak into _personalisation_base_attributes.
    // We use only an exact groupId match here — no fallback — so that other slot types
    // (e.g. ALPHANUMERIC hot foil) whose visData entries share the same reduce are not
    // incorrectly skipped just because the only IMAGE_UPLOAD choice happens to be a photo variant.
    const isPhotoVariantEntry = personalisationChoices.some(
      (choice) =>
        choice.slotType === 'IMAGE_UPLOAD' &&
        choice.visualisationGroupId === vis.groupId &&
        typeof choice.choice === 'string' &&
        choice.choice.includes(':')
    );
    if (isPhotoVariantEntry) return acc;

    acc[featuredImageKey] = vis.file || vis.url;
    // Find matching ALPHANUMERIC choice by groupId to track which slot this preview belongs to.
    const matchingAlphanumericChoice = personalisationChoices.find(
      (choice) => choice.slotType === 'ALPHANUMERIC' && choice.visualisationGroupId === vis.groupId
    );
    if (matchingAlphanumericChoice) {
      featuredImageMapping[featuredImageKey] = {
        groupId: vis.groupId,
        lineItemPropertyKey: matchingAlphanumericChoice.lineItemPropertyKey,
        supportingImageLineItemPropertyKey: matchingAlphanumericChoice.supportingImageLineItemPropertyKey,
        slotType: 'ALPHANUMERIC'
      };
    }
    return acc;
  }, {});

  const supportingImageProperties = selections?.slots?.reduce(
    (acc, slot) => {
      if (slot.croppedImage instanceof File) {
        const isPhotoVariant = typeof slot.photoVariant === 'string' && !!slot.photoVariant;
        if (isPhotoVariant) {
          // For photo variant slots the raw uploaded/cropped image is what gets physically
          // printed on the insert, so it belongs at the primary LIP key.
          // The visualiser (composited mock-up) is assigned to the supporting key above.
          if (slot.lineItemPropertyKey) {
            acc[slot.lineItemPropertyKey] = slot.croppedImage;
          }
        } else {
          const imageKey = slot.supportingImageLineItemPropertyKey ?? slot.lineItemPropertyKey;
          if (imageKey) acc[`${imageKey}`] = slot.croppedImage;
        }
      }
      return acc;
    },
    {} as Record<string, File>
  );

  const imageProps = {
    // Split visData into URL images (for featured_image) and File objects (for IMAGE_UPLOAD).
    // URLs go to _featured_image properties, Files will be added below.
    ...featuredImageProperties,
    // Add File objects from visData directly to properties.
    // This ensures the File objects are available for upload.
    // Use cropped image from slot if available, otherwise use the visData file.
    ...visData
      .filter((blob) => {
        // Filter for items where url is a File or file is a File
        const urlIsFile = typeof blob.url !== 'string' && blob.url instanceof File;
        const fileIsFile = blob.file instanceof File;
        return urlIsFile || fileIsFile;
      })
      .reduce(
        (acc, blob) => {
          // Find which IMAGE_UPLOAD choice this File belongs to by matching groupId with visualisationGroupId.
          let matchingChoice = personalisationChoices.find(
            (choice) => choice.slotType === 'IMAGE_UPLOAD' && choice.visualisationGroupId === blob.groupId
          );

          // Fallback: If no match found and there's only one IMAGE_UPLOAD choice, use it.
          // This supports legacy cases where visualisationGroup might not be set.
          if (!matchingChoice) {
            const imageUploadChoices = personalisationChoices.filter((choice) => choice.slotType === 'IMAGE_UPLOAD');
            if (imageUploadChoices.length === 1) {
              matchingChoice = imageUploadChoices[0];
            }
          }

          // Get the file from blob (either url or file property)
          let processedFile: File | null = null;
          if (blob.file instanceof File) {
            processedFile = blob.file;
          } else if (typeof blob.url !== 'string') {
            // At this point, url must be a File (filtered above)
            processedFile = blob.url as File;
          }

          if (!processedFile) {
            // Skip if we don't have a valid file
            return acc;
          }

          // Detect photo variant IMAGE_UPLOAD choices (choice is "{slotId}:{variantId}").
          const isPhotoVariantChoice =
            matchingChoice?.slotType === 'IMAGE_UPLOAD' &&
            typeof matchingChoice?.choice === 'string' &&
            matchingChoice.choice.includes(':');

          if (isPhotoVariantChoice) {
            // For photo variant slots the visData file is the composited visualiser (product mock-up).
            // It belongs on the supporting image key so the warehouse has a visual reference,
            // while the primary LIP key is reserved for the raw uploaded image (handled in
            // supportingImageProperties below). We also skip blob.lineItemPropertyKey here to
            // avoid generating an unwanted extra LIP like "Photo Insert - Visual".
            if (matchingChoice?.supportingImageLineItemPropertyKey) {
              acc[matchingChoice.supportingImageLineItemPropertyKey] = processedFile;
            }
          } else {
            // Regular IMAGE_UPLOAD: visualiser goes to the primary LIP key.
            if (matchingChoice?.lineItemPropertyKey) {
              acc[matchingChoice.lineItemPropertyKey] = processedFile;
            }
            // Additional feature images (e.g. "Photo Insert - Visual") from the visualiser blob.
            if (blob.lineItemPropertyKey) {
              acc[blob.lineItemPropertyKey] = processedFile;
            }
          }
          return acc;
        },
        {} as Record<string, File>
      ),
    // Add original uploaded images from IMAGE_UPLOAD choices as separate properties.
    // These are needed for warehouse processing and get their own CDN URLs.
    ...supportingImageProperties
  };

  return {
    featuredImageMapping,
    featuredImages: imageProps
  };
};
